
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ReportCard } from "./ReportCard";

interface UsdaReport {
  id: string;
  report_id: string;
  slug_id: string | null;
  slug_name: string | null;
  report_name: string | null;
  published_date: string | null;
  market_types: any;
  commodities: string[] | null;
  varieties: string[] | null;
  market_cities: string[] | null;
  report_type: string | null;
  created_at: string;
  updated_at: string;
}

interface ProfileReport {
  id: string;
  profile_id: string;
  report_id: string;
  market_city: string;
  relevance_score: number;
  report_name: string | null;
  market_types: any;
  report_type: string | null;
  created_at: string;
  updated_at: string;
  usda_reports: UsdaReport;
}

interface ReportTabsProps {
  userReports: ProfileReport[];
}

export const ReportTabs = ({ userReports }: ReportTabsProps) => {
  const groupReportsByType = (reports: ProfileReport[]) => {
    return reports.reduce((acc, report) => {
      const reportType = report.usda_reports?.report_type || 'other';
      if (!acc[reportType]) {
        acc[reportType] = [];
      }
      acc[reportType].push(report);
      return acc;
    }, {} as Record<string, ProfileReport[]>);
  };

  const groupedUserReports = groupReportsByType(userReports);

  return (
    <Tabs defaultValue="all" className="w-full">
      <div className="mb-4">
        <TabsList className="grid w-full grid-cols-4 mb-2">
          <TabsTrigger value="all" className="text-xs">
            All ({userReports.length})
          </TabsTrigger>
          <TabsTrigger value="terminal market prices" className="text-xs">
            Terminal ({groupedUserReports['terminal market prices']?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="wholesale market" className="text-xs">
            Wholesale ({groupedUserReports['wholesale market']?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="shipping point prices" className="text-xs">
            Shipping ({groupedUserReports['shipping point prices']?.length || 0})
          </TabsTrigger>
        </TabsList>
        
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="daily movement" className="text-xs">
            Movement ({groupedUserReports['daily movement']?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="prices" className="text-xs">
            Prices ({groupedUserReports['prices']?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="other" className="text-xs">
            Other ({groupedUserReports['other']?.length || 0})
          </TabsTrigger>
        </TabsList>
      </div>
      
      <TabsContent value="all" className="space-y-4 mt-4">
        {userReports.slice(0, 10).map((profileReport) => (
          <ReportCard 
            key={profileReport.id} 
            report={profileReport} 
            isProfileReport={true}
          />
        ))}
        {userReports.length > 10 && (
          <div className="text-center py-4">
            <p className="text-sm text-gray-600">
              Showing 10 of {userReports.length} reports. Use the tabs above to filter by report type.
            </p>
          </div>
        )}
      </TabsContent>

      {Object.entries(groupedUserReports).map(([reportType, reports]) => (
        <TabsContent key={reportType} value={reportType} className="space-y-4 mt-4">
          {reports.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No {reportType} reports found for your region</p>
            </div>
          ) : (
            reports.map((report) => (
              <ReportCard 
                key={report.id} 
                report={report} 
                isProfileReport={true}
              />
            ))
          )}
        </TabsContent>
      ))}
    </Tabs>
  );
};
